import React, { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, Link } from "react-router-dom";
import Tabs from "../../Components/Tabs";
import DataTable from "react-data-table-component";
import { createColumns } from "../../helpers";
import { DATATABLE_CUSTOM_STYLES } from "../../constants/datatableStyles";
import PageTitle from "../../Components/UI/PageTitle";
import EyeDetailsIcon from "../../Components/Svgs/EyeDetailsIcon";
import RowLoader from "../../Components/UI/RowLoader";
import ErrorAnimationComponent from "../../assets/lottie/ErrorAnimation/ErrorAnimationComponent";
import LightButton from "../../Components/UI/LightButton";
import { FetchPackageManagement } from "../../Redux/PackageManagement/PackageManagementActions";
import { clearPackageManagementData } from "../../Redux/PackageManagement/PackageManagementSlice";

const PackageManagement = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const ACCOUNTS_REVIEW_TABS = [
    {
      key: "partners",
      label: "By Partners Type",
    },
    {
      key: "categories",
      label: "By Categories",
    },
  ];
  const [currentTab, setCurrentTab] = useState(
    new URLSearchParams(location.search).get("tab") || "partners"
  );
  const [sortKey, setSortKey] = useState(
    new URLSearchParams(location.search).get("sortKey") || ""
  );
  const [sortDirection, setSortDirection] = useState(
    new URLSearchParams(location.search).get("sortDirection") || ""
  );

  const { packageManagementData, totalPartnersInPackages, isLoading, error } =
    useSelector((state) => state.packageManagement);

  useEffect(() => {
    dispatch(FetchPackageManagement(currentTab));

    return () => dispatch(clearPackageManagementData());
  }, [currentTab, sortKey, sortDirection, dispatch]);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set("tab", currentTab);

    if (sortKey) params.set("sortKey", sortKey);
    if (sortDirection) params.set("sortDirection", sortDirection);
    navigate({ search: params.toString() });
  }, [currentTab, sortKey, sortDirection, navigate]);

  const handleTabClick = (key) => {
    setCurrentTab(key);
  };

  const handleSortChange = (column, direction) => {
    setSortKey(column.sortKey);
    setSortDirection(direction);
  };

  const filterAndSortData = () => {
    return packageManagementData;
  };

  const hiddenColumns = ["id"];

  const customColumns =
    currentTab === "partners" || currentTab === "categories"
      ? [
          ...[
            {
              name: "Details",
              selector: (row) => row.name + " - custom",
              ui: (row) => (
                <div className="tw-flex tw-gap-1">
                  <Link
                    to={`${row.id}?filterBy=${
                      currentTab === "partners" ? "partner_type" : "category"
                    }`}
                  >
                    <EyeDetailsIcon width="30px" height="30px" />
                  </Link>
                </div>
              ),
            },
          ],
        ]
      : [];

  const columns = useMemo(() => {
    return createColumns(
      packageManagementData,
      customColumns,
      hiddenColumns,
      true
    );
  }, [packageManagementData]).map((col) => ({
    ...col,
    sortable: false,
    width: col.name === "Num" ? "70px" : "1fr",
  }));

  if (error) return <ErrorAnimationComponent message={error} />;

  return (
    <>
      <div className="tw-flex max-md:tw-flex-col justify-content-between tw-items-center">
        <PageTitle title="Package Management" />
        <LightButton
          hoverColor="hover:tw-text-blueLight"
          text={"Add New Package"}
          icon={<span className="material-icons">add</span>}
          to={"/add-new-package"}
        />
      </div>
      <div className="bg-white flex flex-col mt-4">
        <Tabs
          options={ACCOUNTS_REVIEW_TABS}
          onTabClick={handleTabClick}
          currentTab={currentTab}
        />

        <div className="bg-white shadow-lg rounded-lg flex flex-col p-2 gap-2 mb-5">
          <DataTable
            columns={columns}
            data={filterAndSortData()}
            customStyles={DATATABLE_CUSTOM_STYLES}
            progressPending={isLoading}
            onSort={handleSortChange}
            progressComponent={<RowLoader />}
            sortServer
          />

          {!isLoading && (
            <>
              <div className="tw-flex tw-pt-2 tw-text-center tw-border-t-2 tw-border-[#646464]">
                <div className="tw-w-[70px]"></div>
                <div className="tw-flex-1">Total</div>
                <div className="tw-flex-1">{totalPartnersInPackages || 0}</div>
                <div className="tw-flex tw-justify-center tw-gap-1 tw-flex-1">
                  <Link
                    to={`all?filterBy=${
                      currentTab === "partners"
                        ? "total_partner_type"
                        : "total_category"
                    }`}
                  >
                    <EyeDetailsIcon width="30px" height="30px" />
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default PackageManagement;
